import {
  POLLUTANT_UNITS,
  nextAvailablePollutantName,
  type Pollutant,
} from "../model/inp/pollutants.ts";
import { useT } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

export function PollutantsEditor({
  pollutants,
  onChange,
  selectedName,
  onSelect,
}: {
  pollutants: Pollutant[];
  onChange: (pollutants: Pollutant[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deletePollutant,
    rename: renameSelected,
    update,
  } = useNamedCollection(pollutants, onChange, selectedName, onSelect);

  function addPollutant() {
    const name = nextAvailablePollutantName(new Set(pollutants.map((p) => p.name)));
    add({
      name,
      units: POLLUTANT_UNITS[0],
      rainConc: 0,
      gwConc: 0,
      iiConc: 0,
      decayCoeff: 0,
      snowOnly: "NO",
      coPollutant: "*",
      coFraction: 0,
      dwfConc: 0,
      initConc: 0,
    });
  }

  function set<K extends keyof Pollutant>(key: K, value: Pollutant[K]) {
    update((p) => ({ ...p, [key]: value }));
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={pollutants}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addPollutant}
        addLabel={t("projectData.addPollutant")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("projectData.noPollutantSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deletePollutant(selected.name)}
              deleteLabel={t("projectData.deletePollutant")}
            />
            <div className="named-collection-fields">
              <label className="options-field">
                {t("projectData.pollutantUnits")}
                <select value={selected.units} onChange={(e) => set("units", e.target.value)}>
                  {POLLUTANT_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </label>
              <label className="options-field">
                {t("projectData.pollutantRainConc")}
                <NumberInput value={selected.rainConc} onChange={(v) => set("rainConc", v)} />
              </label>
              <label className="options-field">
                {t("projectData.pollutantGwConc")}
                <NumberInput value={selected.gwConc} onChange={(v) => set("gwConc", v)} />
              </label>
              <label className="options-field">
                {t("projectData.pollutantIiConc")}
                <NumberInput value={selected.iiConc} onChange={(v) => set("iiConc", v)} />
              </label>
              <label className="options-field">
                {t("projectData.pollutantDecayCoeff")}
                <NumberInput value={selected.decayCoeff} onChange={(v) => set("decayCoeff", v)} />
              </label>
              <label className="options-field">
                {t("projectData.pollutantSnowOnly")}
                <select value={selected.snowOnly} onChange={(e) => set("snowOnly", e.target.value)}>
                  <option value="NO">{t("option.no")}</option>
                  <option value="YES">{t("option.yes")}</option>
                </select>
              </label>
              <label className="options-field">
                {t("projectData.pollutantCoPollutant")}
                <input
                  type="text"
                  value={selected.coPollutant}
                  onChange={(e) => set("coPollutant", e.target.value)}
                />
              </label>
              <label className="options-field">
                {t("projectData.pollutantCoFraction")}
                <NumberInput value={selected.coFraction} onChange={(v) => set("coFraction", v)} />
              </label>
              <label className="options-field">
                {t("projectData.pollutantDwfConc")}
                <NumberInput value={selected.dwfConc} onChange={(v) => set("dwfConc", v)} />
              </label>
              <label className="options-field">
                {t("projectData.pollutantInitConc")}
                <NumberInput value={selected.initConc} onChange={(v) => set("initConc", v)} />
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
